import { getSettingsSearchTextSegments } from './device_settings_search_helpers';

const SETTINGS_SEARCH_CONTENT_HIGHLIGHT = 'settings-search-content-highlight';
const SETTINGS_SEARCH_CONTENT_HIGHLIGHT_ATTRIBUTE = 'data-settings-search-content-highlight';

type HighlightRegistry = {
	delete: (name: string) => void;
	set: (name: string, highlight: unknown) => void;
};

type HighlightConstructor = new (...ranges: Range[]) => unknown;

const getHighlightApi = (): {
	HighlightClass?: HighlightConstructor;
	registry?: HighlightRegistry;
} => {
	const cssApi = typeof CSS === 'undefined' ? undefined : CSS;
	return {
		HighlightClass: (globalThis as typeof globalThis & { Highlight?: HighlightConstructor })
			.Highlight,
		registry: (cssApi as (typeof CSS & { highlights?: HighlightRegistry }) | undefined)?.highlights,
	};
};

const getTextNodeRanges = (node: HTMLElement, query: string): Range[] => {
	const ranges: Range[] = [];
	const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
	let textNode = walker.nextNode();

	while (textNode) {
		const text = textNode.textContent ?? '';
		let offset = 0;
		for (const segment of getSettingsSearchTextSegments(text, query)) {
			const endOffset = offset + segment.text.length;
			if (segment.highlighted) {
				const range = document.createRange();
				range.setStart(textNode, offset);
				range.setEnd(textNode, endOffset);
				ranges.push(range);
			}
			offset = endOffset;
		}
		textNode = walker.nextNode();
	}

	return ranges;
};

const clearFallbackHighlights = (node: HTMLElement) => {
	const highlights = node.querySelectorAll<HTMLElement>(
		`mark[${SETTINGS_SEARCH_CONTENT_HIGHLIGHT_ATTRIBUTE}]`,
	);
	const parents = new Set<ParentNode>();
	for (const highlight of highlights) {
		if (highlight.parentNode) parents.add(highlight.parentNode);
		highlight.replaceWith(document.createTextNode(highlight.textContent ?? ''));
	}
	for (const parent of parents) parent.normalize();
};

const applyFallbackHighlights = (node: HTMLElement, query: string) => {
	const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
	const textNodes: Text[] = [];
	let textNode = walker.nextNode();
	while (textNode) {
		const parentTagName = textNode.parentElement?.tagName;
		if (
			textNode instanceof Text &&
			parentTagName &&
			!['OPTION', 'SCRIPT', 'STYLE', 'TEXTAREA'].includes(parentTagName)
		) {
			textNodes.push(textNode);
		}
		textNode = walker.nextNode();
	}

	for (const currentTextNode of textNodes) {
		const segments = getSettingsSearchTextSegments(currentTextNode.textContent ?? '', query);
		if (!segments.some((segment) => segment.highlighted)) continue;

		const fragment = document.createDocumentFragment();
		for (const segment of segments) {
			if (!segment.highlighted) {
				fragment.append(document.createTextNode(segment.text));
				continue;
			}
			const highlight = document.createElement('mark');
			highlight.setAttribute(SETTINGS_SEARCH_CONTENT_HIGHLIGHT_ATTRIBUTE, '');
			highlight.className = 'settings-search-content-highlight';
			highlight.textContent = segment.text;
			fragment.append(highlight);
		}
		currentTextNode.replaceWith(fragment);
	}
};

export function highlightSettingsSearchContent(node: HTMLElement, initialQuery: string) {
	let query = initialQuery;
	let updateQueued = false;
	let destroyed = false;
	let observer: MutationObserver;

	const observeContentChanges = () => {
		observer.observe(node, { childList: true, characterData: true, subtree: true });
	};

	const updateHighlight = () => {
		updateQueued = false;
		if (destroyed) return;
		observer.disconnect();
		clearFallbackHighlights(node);
		const { HighlightClass, registry } = getHighlightApi();
		registry?.delete(SETTINGS_SEARCH_CONTENT_HIGHLIGHT);
		if (query.trim()) {
			if (HighlightClass && registry) {
				const ranges = getTextNodeRanges(node, query);
				if (ranges.length > 0) {
					registry.set(SETTINGS_SEARCH_CONTENT_HIGHLIGHT, new HighlightClass(...ranges));
				}
			} else {
				applyFallbackHighlights(node, query);
			}
		}
		observer.takeRecords();
		observeContentChanges();
	};

	const queueHighlightUpdate = () => {
		if (updateQueued) return;
		updateQueued = true;
		queueMicrotask(updateHighlight);
	};

	observer = new MutationObserver(queueHighlightUpdate);
	observeContentChanges();
	queueHighlightUpdate();

	return {
		update(nextQuery: string) {
			query = nextQuery;
			queueHighlightUpdate();
		},
		destroy() {
			destroyed = true;
			observer.disconnect();
			getHighlightApi().registry?.delete(SETTINGS_SEARCH_CONTENT_HIGHLIGHT);
			clearFallbackHighlights(node);
		},
	};
}
